import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageShell from "@/components/site/PageShell";
import useSEO from "@/lib/useSEO";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { BookOpen, FileText, Calculator, Lightbulb, ScrollText, ArrowRight } from "lucide-react";
import { articles } from "@/data/articles";
import { faqCategories } from "@/data/faqs";

const CATEGORIES = [
  { i: ScrollText, t: "OBBBA", d: "Everything the One Big Beautiful Bill Act changed for cost segregation, bonus depreciation, and the short-term rental loophole." },
  { i: Calculator, t: "Cost Segregation", d: "Engineering studies, look-back via Form 3115, and how OBBBA reshaped year-one cash impact." },
  { i: Lightbulb, t: "R&D Tax Credit", d: "Four-part test, qualified research expenses, payroll-offset option, state credits, and audit defense." },
  { i: FileText, t: "Case Studies", d: "Real client deliverables, sample reports, and outcomes across property types and industries." },
];

export default function Knowledge() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "faqs" ? "faqs" : "articles";
  const setTab = (t) => setSearchParams(t === "faqs" ? { tab: "faqs" } : {});

  useSEO({
    title:
      tab === "faqs"
        ? "FAQs | Cost Segregation & R&D Tax Credit Questions | IncentTax"
        : "Knowledge Center | Cost Segregation & R&D Tax Credit Insights | IncentTax",
    description:
      tab === "faqs"
        ? "Frequently asked questions about cost segregation studies, R&D tax credits, OBBBA bonus depreciation, look-back studies, audit defense, and CPA partnerships."
        : "Articles and guides on cost segregation studies, R&D tax credits, OBBBA bonus depreciation, the short-term rental loophole, look-back studies, and more.",
    canonical: "https://incenttax.com/knowledge",
  });

  return (
    <PageShell testid="knowledge-page">
      {/* HERO */}
      <section className="bg-forest-900 text-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 pt-24 sm:pt-32 pb-16">
          <p className="text-brass uppercase tracking-[0.28em] text-sm mb-6">Knowledge Center</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight">
            Specialty tax,
            <br /> <span className="italic text-brass">demystified.</span>
          </h1>
          <p className="mt-8 text-ivory/85 text-lg sm:text-xl leading-relaxed max-w-3xl">
            Plain-English explainers, technical deep-dives, and answers to the
            questions we hear most — on OBBBA, cost segregation, and the R&D
            credit.
          </p>

          {/* TABS */}
          <div className="mt-14 flex gap-0 border-b border-ivory/15" role="tablist">
            <button
              role="tab"
              aria-selected={tab === "articles"}
              onClick={() => setTab("articles")}
              data-testid="knowledge-tab-articles"
              className={`px-8 py-4 text-sm uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${
                tab === "articles"
                  ? "border-brass text-brass"
                  : "border-transparent text-ivory/60 hover:text-ivory"
              }`}
            >
              Articles
            </button>
            <button
              role="tab"
              aria-selected={tab === "faqs"}
              onClick={() => setTab("faqs")}
              data-testid="knowledge-tab-faqs"
              className={`px-8 py-4 text-sm uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${
                tab === "faqs"
                  ? "border-brass text-brass"
                  : "border-transparent text-ivory/60 hover:text-ivory"
              }`}
            >
              FAQs
            </button>
          </div>
        </div>
      </section>

      {tab === "articles" ? <ArticlesTab /> : <FaqsTab />}

      {/* CTA */}
      <section className="py-24 bg-forest-900 text-ivory">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <BookOpen className="text-brass mx-auto mb-6" size={40} strokeWidth={1.5} />
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory leading-tight mb-6">
            Question we haven't covered?
          </h2>
          <p className="text-ivory/80 text-lg mb-10 max-w-2xl mx-auto">
            Send it our way — we'll answer it directly, and probably turn it into a public article so the next person finds the answer faster.
          </p>
          <Link
            to="/#contact"
            className="inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-10 py-4 font-medium"
            data-testid="knowledge-bottom-cta"
          >
            Ask a Question <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}

function ArticlesTab() {
  return (
    <>
      {/* CATEGORIES */}
      <section className="py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">Topics</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 mb-12 max-w-4xl leading-tight">
            Where would you like to dig in?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-ivory-400 border border-ivory-400">
            {CATEGORIES.map(({ i: Icon, t, d }) => (
              <div
                key={t}
                className="bg-white p-8 hover:bg-ivory-100 transition-colors group"
                data-testid={`knowledge-cat-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <Icon className="text-brass mb-5" size={28} strokeWidth={1.5} />
                <div className="font-serif text-xl text-forest-900 mb-3">{t}</div>
                <p className="text-charcoal text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ARTICLES */}
      <section className="py-24 bg-ivory-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">Featured articles</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 mb-12 max-w-4xl leading-tight">
            OBBBA & cost segregation — <span className="italic text-brass">the latest from our team.</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-px bg-ivory-400 border border-ivory-400">
            {articles.map((a, i) => (
              <Link
                key={a.slug}
                to={`/knowledge/${a.slug}`}
                className="bg-white p-10 flex flex-col hover:bg-ivory-100 transition-colors group"
                data-testid={`knowledge-article-${i}`}
              >
                <div className="flex items-center justify-between mb-4 text-xs uppercase tracking-[0.22em]">
                  <span className="text-brass">{a.category}</span>
                  <span className="text-charcoal-light">{a.readTime}</span>
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-forest-900 mb-4 leading-snug group-hover:text-brass transition-colors">
                  {a.title}
                </h3>
                <p className="text-charcoal text-base leading-relaxed mb-6 flex-1">{a.excerpt}</p>
                <span className="inline-flex items-center gap-2 text-brass text-sm font-medium">
                  Read article <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function FaqsTab() {
  const [activeCat, setActiveCat] = useState(0);
  const cat = faqCategories[activeCat];

  return (
    <section className="py-24 bg-ivory" data-testid="knowledge-faqs-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">Frequently asked questions</p>
        <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 mb-12 max-w-4xl leading-tight">
          What can we <span className="italic text-brass">help you with?</span>
        </h2>

        <div className="grid lg:grid-cols-12 gap-12">
          {/* TOPIC NAV */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 border border-ivory-400 bg-white divide-y divide-ivory-400">
              {faqCategories.map((c, i) => (
                <button
                  key={c.slug}
                  onClick={() => setActiveCat(i)}
                  data-testid={`faq-category-${c.slug}`}
                  className={`w-full flex items-center justify-between text-left px-6 py-5 transition-colors ${
                    i === activeCat
                      ? "bg-forest-900 text-ivory"
                      : "text-forest-900 hover:bg-ivory-100"
                  }`}
                >
                  <span className="font-serif text-lg">{c.label}</span>
                  <span
                    className={`text-xs tabular ${
                      i === activeCat ? "text-brass" : "text-charcoal-light"
                    }`}
                  >
                    {c.items.length}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* QUESTIONS */}
          <div className="lg:col-span-8">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="font-serif text-2xl text-forest-900">{cat.label}</h3>
              <span className="text-charcoal-light text-sm">
                {cat.items.length} questions
              </span>
            </div>
            <Accordion type="single" collapsible className="border-t border-ivory-400" key={cat.slug}>
              {cat.items.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border-b border-ivory-400 border-t-0"
                >
                  <AccordionTrigger className="py-6 text-left text-forest-900 font-serif text-xl hover:no-underline hover:text-brass">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-charcoal text-base leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
